const DB_NAME = "offlineFilesDB";
const STORE_NAME = "files";
const DB_VERSION = 1;

const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
};

const saveToIndexedDB = async (key: string, data: Blob): Promise<void> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(data, key);

    request.onerror = () => {
      transaction.abort();
    };

    transaction.onabort = () => {
      db.close();
      reject(
        request.error ??
          transaction.error ??
          new Error("IndexedDB transaction aborted")
      );
    };

    transaction.oncomplete = () => {
      db.close();
      resolve();
    };
  });
};

const getFromIndexedDB = async (key: string): Promise<Blob | undefined> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(key);

    request.onerror = () => {
      reject(request.error);
    };

    request.onsuccess = () => {
      const result = request.result as Blob | string;
      if (result instanceof Blob) {
        resolve(result);
      } else if (typeof result === "string") {
        resolve(new Blob([result]));
      } else {
        resolve(undefined);
      }
    };

    transaction.onabort = () => {
      db.close();
      reject(
        request.error ??
          transaction.error ??
          new Error("IndexedDB transaction aborted")
      );
    };

    transaction.oncomplete = () => {
      db.close();
    };
  });
};

const hasInIndexedDB = async (key: string): Promise<boolean> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.count(key);

    request.onerror = () => {
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result > 0);
    };

    transaction.onabort = () => {
      db.close();
      reject(
        request.error ??
          transaction.error ??
          new Error("IndexedDB transaction aborted")
      );
    };

    transaction.oncomplete = () => {
      db.close();
    };
  });
};

const deleteFromIndexedDB = async (key: string): Promise<void> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(key);

    request.onerror = () => {
      transaction.abort();
    };

    transaction.onabort = () => {
      db.close();
      reject(
        request.error ??
          transaction.error ??
          new Error("IndexedDB transaction aborted")
      );
    };

    transaction.oncomplete = () => {
      db.close();
      resolve();
    };
  });
};

export {
  deleteFromIndexedDB,
  getFromIndexedDB,
  hasInIndexedDB,
  saveToIndexedDB,
};
